'use client'

import { useTranslations } from 'next-intl'

import { CookieConsentValues } from './CookieConstent.types'
import { Button, Switch, Text } from '@/components'
import { useCallback } from 'react'

interface Props {
  consent: CookieConsentValues
  setConsent: (val: CookieConsentValues) => void
  onSave: () => void
  onCancel?: () => void
}

const CookieSettingsPanel = ({
  consent,
  setConsent,
  onSave,
  onCancel,
}: Props) => {
  const t = useTranslations('cookieConsent.cookieSettingsPanel')

  const toggle = useCallback(
    (key: keyof CookieConsentValues) => {
      if (key === 'necessary') return
      setConsent({ ...consent, [key]: !consent[key] })
    },
    [consent, setConsent],
  )

  const renderRow = (key: keyof CookieConsentValues, readOnly = false) => (
    <div className="flex items-center justify-between p-4 bg-glow/3 border border-white/10 rounded-xl">
      <div className="max-w-md pr-4">
        <p className="font-semibold text-white">{t(`${key}.title`)}</p>
        <p className="text-sm text-gray-300">{t(`${key}.description`)}</p>
      </div>
      <Switch
        defaultChecked={consent[key]}
        onCheckedChange={() => toggle(key)}
        disabled={readOnly}
      />
    </div>
  )

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="w-full max-w-2xl max-h-screen p-8 space-y-6 overflow-y-auto bg-primary shadow-xl rounded-2xl">
        <div>
          <Text variant="headingXl" className="text-white mb-6">
            {t('title')}
          </Text>
          <p className="mt-2 text-sm text-gray-200">{t('intro1')}</p>
          <p className="mt-2 text-sm text-gray-300">{t('intro2')}</p>
          <p className="mt-2 text-sm text-gray-300">
            {t('domains')}
            <br />• cookieinfo.net
            <br />• intranet.cookieinfo.net
          </p>
        </div>

        <div className="space-y-3">
          {renderRow('necessary', true)}
          {renderRow('statistic')}
          {renderRow('preferences')}
          {renderRow('marketing')}
        </div>

        <div className="flex justify-end gap-4 pt-4">
          {onCancel && (
            <Button onClick={onCancel} variant="link" className="text-white">
              {t('cancel')}
            </Button>
          )}
          <Button onClick={onSave} variant="glow">
            {t('save')}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default CookieSettingsPanel
